import { useState } from 'react';
import { useTranslations } from 'use-intl';
import type { SessionInput } from '@shared/schemas';
import { type AiSessionPlan, useAiStatus, useSessionPlan } from '@/app/queries';
import { Button } from '@/ui/Button';
import { Field, TextInput } from '@/ui/Field';
import { Notice } from '@/ui/Panel';
import { AiAnswerFrame, AiRefusal } from '@/ui/AiAnswer';
import { Chip } from '@/ui/Chip';

/**
 * Drafting a session, not writing one.
 *
 * The draft lands in the form as a starting point the coach then edits, and it
 * is never saved on his behalf — the chain this product is built on only means
 * something if a human put each link in it. So the flow is suggest, read the
 * reasoning, take it or leave it, and the button says "use this draft" rather
 * than "create session".
 *
 * It needs the objective in the coach's own words rather than taking the week's
 * automatically, because the session is the level where a week objective turns
 * into something specific, and that sentence is the coach's judgement.
 */
export function AiPlanner({
  teamId,
  onDate,
  durationMin,
  suggestedObjective,
  onUse,
}: {
  teamId: string;
  onDate: string;
  durationMin: number;
  suggestedObjective?: string | null;
  onUse: (patch: Partial<SessionInput>) => void;
}) {
  const t = useTranslations();
  const status = useAiStatus();
  const plan = useSessionPlan();
  const [objective, setObjective] = useState(suggestedObjective ?? '');
  const [open, setOpen] = useState(false);

  if (!status.data) return null;
  if (!status.data.enabled || !status.data.inPlan) {
    return (
      <Notice>
        {status.data.inPlan ? t('ai.refused.ai_switched_off') : t('ai.refused.ai_not_in_plan')}
      </Notice>
    );
  }

  const answer = plan.data;
  const refusal = plan.error as { code?: string } | null;

  return (
    <div className="grid gap-3">
      {!open ? (
        <div className="flex flex-wrap items-center gap-3">
          <Button type="button" variant="secondary" onClick={() => setOpen(true)}>
            {t('ai.draftSession')}
          </Button>
          <span className="tabular text-meta text-ink-faint">
            {t('ai.balanceLeft', { count: status.data.credits })}
          </span>
        </div>
      ) : (
        <div className="grid gap-3 sm:grid-cols-[1fr_auto] sm:items-end">
          <Field label={t('ai.objectiveLabel')} hint={t('ai.objectiveHint')}>
            {(p) => (
              <TextInput
                {...p}
                value={objective}
                placeholder={t('ai.objectivePlaceholder')}
                onChange={(e) => setObjective(e.target.value)}
              />
            )}
          </Field>
          <Button
            type="button"
            variant="primary"
            busy={plan.isPending}
            disabled={objective.trim().length < 3}
            onClick={() => {
              plan.mutate({ teamId, onDate, objective: objective.trim(), durationMin });
            }}
          >
            {t('ai.draft')}
          </Button>
        </div>
      )}

      {plan.isPending ? <Notice>{t('ai.thinking')}</Notice> : null}
      {refusal?.code ? <AiRefusal code={refusal.code} /> : null}

      {answer ? (
        <div className="overflow-hidden rounded-(--radius-panel) border border-line bg-surface">
          <AiAnswerFrame
            answer={answer}
            action={
              <Button
                type="button"
                variant="primary"
                onClick={() =>
                  onUse({
                    title: answer.result.title,
                    objective: answer.result.objective,
                    rationale: answer.result.rationale,
                    plannedDurationMin: sumMinutes(answer.result),
                    exercises: answer.result.exercises.map((e) => ({
                      name: e.name,
                      categoryKey: e.categoryKey,
                      durationMin: e.durationMin,
                      workSeconds: e.workSeconds,
                      restSeconds: e.restSeconds,
                      reps: e.reps,
                      sets: e.sets,
                      rationale: e.rationale,
                      notes: e.objective,
                    })),
                  } as Partial<SessionInput>)
                }
              >
                {t('ai.useDraft')}
              </Button>
            }
          >
            <div className="grid gap-3">
              <div>
                <h3 className="font-condensed text-subhead font-semibold tracking-tight">
                  {answer.result.title}
                </h3>
                <p className="mt-1 text-ink-muted">{answer.result.objective}</p>
              </div>

              <ul className="grid gap-2">
                {answer.result.exercises.map((e, i) => (
                  <li key={`${e.name}-${i}`} className="border-s-2 border-line ps-3">
                    <div className="flex flex-wrap items-baseline gap-2">
                      <span className="font-medium">{e.name}</span>
                      <span className="tabular text-meta text-ink-muted">
                        {t('ai.minutes', { count: e.durationMin })}
                      </span>
                      <Chip muted>{e.categoryKey}</Chip>
                      {e.players ? (
                        <span className="text-meta text-ink-faint">{e.players}</span>
                      ) : null}
                    </div>
                    <p className="text-meta text-ink-muted">{e.objective}</p>
                    <p className="text-meta text-ink-faint">{e.rationale}</p>
                  </li>
                ))}
              </ul>
              <p className="tabular text-meta text-ink-faint">
                {t('ai.totalMinutes', {
                  count: sumMinutes(answer.result),
                  planned: durationMin,
                })}
              </p>

              {answer.result.coachingPoints.length > 0 ? (
                <section>
                  <h4 className="text-meta font-semibold text-ink-muted">
                    {t('ai.coachingPoints')}
                  </h4>
                  <ul className="mt-1 grid gap-0.5 text-meta">
                    {answer.result.coachingPoints.map((p) => (
                      <li key={p}>{p}</li>
                    ))}
                  </ul>
                </section>
              ) : null}

              {answer.result.watchFor.length > 0 ? (
                <section>
                  <h4 className="text-meta font-semibold text-ink-muted">{t('ai.watchFor')}</h4>
                  <ul className="mt-1 grid gap-0.5 text-meta">
                    {answer.result.watchFor.map((p) => (
                      <li key={p}>{p}</li>
                    ))}
                  </ul>
                </section>
              ) : null}
            </div>
          </AiAnswerFrame>
        </div>
      ) : null}
    </div>
  );
}

function sumMinutes(plan: AiSessionPlan): number {
  return plan.exercises.reduce((n, e) => n + e.durationMin, 0);
}
